import { bindPromiseAction } from 'app-components/flux/core/actionHelpers';
import {
  reportConfigurationDefaultsSelectors,
  reportConfigurationsActions,
} from 'app-components/flux/report-configurations';
import type {
  OpenReportConfigurationPayload,
  ReportConfiguration,
  PersistentType,
  ReportNameType,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { registerNavigationIntent } from './registry';
import type { NavigationIntent, NavigationResult, NavigationState } from './types';
import {
  getSavedReportDisplayName,
  resolveSavedReportFromIntentArgs,
  type ReportIntentArgs,
} from './reportIntentsSavedReports';

type ReportIntentConfig = {
  name: string;
  description: string;
  reportNames?: ReportNameType[];
};

type ResolveReportNavigationArgs = {
  state: RootState;
  intentArgs?: ReportIntentArgs;
  reportNames?: ReportNameType[];
  openReportConfiguration?: (payload: OpenReportConfigurationPayload) => Promise<ReportConfiguration | undefined>;
};

const MONTHLY_SUMMARY_PATH = '/reports/monthly-summary';
const SAVED_REPORT_NAME_PARAM = 'savedReportName' as const;

const reportIntentConfigs: ReportIntentConfig[] = [
  {
    // Dedicated intent so the LLM has an unambiguous way to open a user-named saved report.
    // This avoids "best guess" fallback to generic spending/income intents when the user
    // clearly asked for a named report.
    name: 'report_saved',
    description:
      'Open a saved/custom report by name. Use only when the user explicitly asks to open or navigate to a named saved report.',
  },
  {
    name: 'report_spending',
    description:
      'Navigate to the Spending report page. Use only when the user explicitly asks to open, go to, or navigate to the spending report — not when they ask about their spending data.',
    reportNames: ['total-spending'],
  },
  {
    name: 'report_income',
    description:
      'Navigate to the Income report page. Use only when the user explicitly asks to open, go to, or navigate to the income report — not when they ask about their income data.',
    reportNames: ['income'],
  },
  {
    name: 'report_income_expense',
    description:
      'Navigate to the Income & Expense report page. Use only when the user explicitly asks to open, go to, or navigate to it.',
    reportNames: ['income-expense'],
  },
  {
    name: 'report_savings',
    description:
      'Navigate to the Savings report page. Use only when the user explicitly asks to open, go to, or navigate to the savings report — not when they ask about their savings data.',
    reportNames: ['savings'],
  },
  {
    name: 'report_net_worth',
    description:
      'Navigate to the Net Worth report page. Use only when the user explicitly asks to open, go to, or navigate to the net worth report — not when they ask about their net worth.',
    reportNames: ['net-worth'],
  },
  {
    name: 'report_monthly_summary',
    description:
      'Navigate to the Monthly Summary report page. Use only when the user explicitly asks to open, go to, or navigate to it.',
  },
  {
    name: 'report_taxes',
    description:
      'Navigate to the Tax report page. Use only when the user explicitly asks to open, go to, or navigate to the tax report — not when they ask about their tax data.',
    reportNames: ['taxes', 'personal-taxes'],
  },
];

const getDefaultConfigForReportNames = (
  state: RootState,
  reportNames: readonly ReportNameType[],
): ReportConfiguration | undefined => {
  const defaultsByName = reportConfigurationDefaultsSelectors.getReportsConfigurationsByName(state);
  for (const reportName of reportNames) {
    const config = defaultsByName.get(reportName);
    if (config) {
      return config;
    }
  }
  return undefined;
};

const openReportConfigurationViaStore = async (
  payload: OpenReportConfigurationPayload,
): Promise<ReportConfiguration | undefined> => {
  if (!store) {
    return undefined;
  }

  const openReportConfiguration = bindPromiseAction<
    OpenReportConfigurationPayload,
    Record<string, never>,
    ReportConfiguration
  >(store.dispatch, reportConfigurationsActions.openReportConfiguration);

  return openReportConfiguration(payload);
};

const openResolvedReport = async (
  openReportConfiguration: (payload: OpenReportConfigurationPayload) => Promise<ReportConfiguration | undefined>,
  parentConfigId: string | undefined,
  parentPersistentType: PersistentType | undefined,
  reportLabel: string,
  navigationState?: NavigationState,
): Promise<NavigationResult> => {
  if (!parentConfigId) {
    return {
      type: 'error',
      reason: `Unable to locate a report configuration for ${reportLabel}.`,
    };
  }

  try {
    const openedConfig = await openReportConfiguration({
      parentConfigId,
      parentPersistentType,
    });

    if (!openedConfig?.id) {
      return {
        type: 'error',
        reason: `Unable to open a report tab for ${reportLabel}.`,
      };
    }

    return {
      type: 'navigate',
      payload: { path: `/reports/${openedConfig.id}`, state: navigationState },
    };
  } catch {
    return {
      type: 'error',
      reason: `Failed to open report tab for ${reportLabel}.`,
    };
  }
};

export const resolveReportNavigation = async ({
  state,
  intentArgs,
  reportNames,
  openReportConfiguration = openReportConfigurationViaStore,
}: ResolveReportNavigationArgs): Promise<NavigationResult> => {
  // 1) Saved/custom report navigation (user-named reports).
  //
  // We try this first because saved reports are more specific than generic "Spending/Income/..." tabs.
  const savedReportResolution = resolveSavedReportFromIntentArgs(state, intentArgs, reportNames);
  if (savedReportResolution.type === 'found') {
    const resolvedSavedReportName = getSavedReportDisplayName(savedReportResolution.config);
    const savedReportId = savedReportResolution.config.id;
    return openResolvedReport(
      openReportConfiguration,
      savedReportId,
      savedReportResolution.config.persistentType,
      `saved report "${resolvedSavedReportName}"`,
      savedReportId
        ? {
            kind: 'saved_report',
            resolvedSavedReport: { id: savedReportId, name: resolvedSavedReportName },
          }
        : undefined,
    );
  }

  if (savedReportResolution.type === 'ambiguous') {
    return {
      type: 'error',
      reason: `Found multiple saved reports matching "${savedReportResolution.candidateName}": ${savedReportResolution.names.join(', ')}.`,
    };
  }

  if (savedReportResolution.type === 'explicit_not_found') {
    return {
      type: 'error',
      reason: `Unable to locate a saved report named "${savedReportResolution.candidateName}".`,
    };
  }

  // 2) Generic report navigation (built-in reports).
  if (!reportNames?.length) {
    return { type: 'navigate', payload: { path: MONTHLY_SUMMARY_PATH } };
  }

  const parentDefaultConfig = getDefaultConfigForReportNames(state, reportNames);
  if (!parentDefaultConfig) {
    return {
      type: 'error',
      reason: `Unable to locate a default report configuration for ${reportNames.join(', ')}.`,
    };
  }

  return openResolvedReport(
    openReportConfiguration,
    parentDefaultConfig.id,
    parentDefaultConfig.persistentType,
    reportNames.join(', '),
  );
};

for (const reportIntentConfig of reportIntentConfigs) {
  const intent: NavigationIntent<ReportIntentArgs, RootState> = {
    name: reportIntentConfig.name,
    description: reportIntentConfig.description,
    params: {
      definitions: [
        {
          name: SAVED_REPORT_NAME_PARAM,
          type: 'string',
          description:
            'Saved/custom report title to open. Use this when the user asks for a named report they created (for example: "Rock and Roll"). Provide only the report title (exclude helper words like "open/go to/show" and the trailing word "report"). Partial titles are OK.',
        },
      ],
    },
    resolver: (_args, state) =>
      resolveReportNavigation({
        state,
        intentArgs: _args,
        reportNames: reportIntentConfig.reportNames,
      }),
  };
  registerNavigationIntent(intent);
}
